import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import { getScreenTimeDateKey, useScreenTime } from '../lib/screenTime';
import { validateProfileDraft } from '../lib/socialUtils';
import {
  getMyProfile,
  updateMyProfile,
  fetchCosmosPosts,
  fetchFriendsPosts,
  fetchGalaxyPosts,
  fetchMyPosts,
  createPost as apiCreatePost,
  toggleReaction as apiToggleReaction,
  fetchMyGalaxies,
  createGalaxy as apiCreateGalaxy,
  fetchFriends as apiFetchFriends,
  addFriend as apiAddFriend,
  removeFriend as apiRemoveFriend,
  fetchNotifications as apiFetchNotifications,
  markNotificationRead as apiMarkNotificationRead,
  fetchPublicGalaxies as apiFetchPublicGalaxies,
  joinGalaxy as apiJoinGalaxy,
} from '../lib/orbitApi';

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const LS_ST = 'orbit_screen_limit';

// Real backend-wired AppContext: replaces the old localStorage mock. auth
// state comes straight from Supabase (magic-link / OTP session, shared
// with the Next.js Orbit build -- same auth.users pool, same
// orbit_profiles table), posts/galaxies/friends are fetched live via
// orbitApi.js instead of INITIAL_POSTS.
export function AppProvider({ children }) {
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  const [posts, setPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [starred, setStarred] = useState({});

  const [galaxies, setGalaxies] = useState([]);
  const [publicGalaxies, setPublicGalaxies] = useState([]);
  const [publicGalaxiesLoading, setPublicGalaxiesLoading] = useState(false);
  const [friends, setFriends] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  const [screenLimit, setScreenLimit] = useState(() => {
    try { return Number(localStorage.getItem(LS_ST)) || 240; } catch { return 240; }
  });
  const { usedMinutes, resetInMs } = useScreenTime(!!authUser);
  const limitToastShown = useRef(false);

  useEffect(() => { localStorage.setItem(LS_ST, String(screenLimit)); }, [screenLimit]);

  useEffect(() => {
    if (!authUser || !screenLimit || usedMinutes < screenLimit) return;
    const toastKey = getScreenTimeDateKey().replace('orbit_screen_usage_', 'orbit_limit_toasted_');
    try {
      if (localStorage.getItem(toastKey)) return;
      localStorage.setItem(toastKey, 'true');
    } catch {
      if (limitToastShown.current) return;
      limitToastShown.current = true;
    }
    toast("You've reached your daily Orbit limit 🌙");
  }, [authUser, screenLimit, usedMinutes]);

  // --- Auth session bootstrap + subscription ---------------------------
  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!mounted) return;
      setAuthUser(session?.user ?? null);
      if (session?.user) {
        const p = await getMyProfile().catch(() => null);
        if (mounted) setProfile(p);
      }
      setAuthLoading(false);
    });

    const { data: sub } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setAuthUser(session?.user ?? null);
      if (session?.user) {
        const p = await getMyProfile().catch(() => null);
        setProfile(p);
      } else {
        setProfile(null);
      }
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // A user is only "logged in" from the app's perspective once they have
  // a real profile row (username etc). If auth succeeded but the profile
  // is still the just-created placeholder or missing, callers check
  // `needsSetup` to route to /setup-profile instead of /home.
  const needsSetup = !!authUser && !profile;

  // Email+password auth. Autoconfirm is enabled on the Supabase auth
  // service, so signUp returns an active session immediately -- no
  // email click required.
  const signup = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signUp({ email, password });
    if (error) throw error;
  }, []);

  const login = useCallback(async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setPosts([]);
    setGalaxies([]);
    setPublicGalaxies([]);
    setFriends([]);
    setNotifications([]);
  }, []);

  const completeProfile = useCallback(async (username, displayName) => {
    const updated = await updateMyProfile({
      username,
      display_name: displayName || username,
    });
    setProfile(updated);
    return updated;
  }, []);

  const saveProfile = useCallback(async (draft) => {
    const fields = validateProfileDraft(draft);
    const updated = await updateMyProfile({
      ...fields,
      avatar_url: draft.avatarUrl || null,
    });
    setProfile(updated);
    return updated;
  }, []);

  // --- Posts -------------------------------------------------------------
  const loadPosts = useCallback(async (tab) => {
    if (!authUser) return;
    setPostsLoading(true);
    try {
      let rows;
      if (tab === 'Friends') {
        rows = await fetchFriendsPosts(authUser.id);
      } else if (tab === 'Galaxies') {
        const myGalaxyIds = galaxies.map((g) => g.id);
        rows = await fetchGalaxyPosts(myGalaxyIds, authUser.id);
      } else {
        rows = await fetchCosmosPosts(authUser.id);
      }
      setPosts(rows);
      setStarred(Object.fromEntries(rows.filter((r) => r.reactedByMe).map((r) => [r.id, true])));
    } finally {
      setPostsLoading(false);
    }
  }, [authUser, galaxies]);

  const loadMyPosts = useCallback(async () => {
    if (!authUser) return [];
    return fetchMyPosts(authUser.id);
  }, [authUser]);

  const addPost = useCallback(async (draft) => {
    const created = await apiCreatePost(draft);
    return created;
  }, []);

  const toggleStar = useCallback(async (postId, initialState = false) => {
    const wasStarred = starred[postId] === undefined ? initialState : !!starred[postId];
    setStarred((s) => ({ ...s, [postId]: !wasStarred }));
    setPosts((list) => list.map((p) => (p.id === postId
      ? { ...p, counts: { ...p.counts, stars: p.counts.stars + (wasStarred ? -1 : 1) } }
      : p)));
    try {
      await apiToggleReaction(postId, wasStarred);
      return !wasStarred;
    } catch (err) {
      // Roll back optimistic update on failure
      setStarred((s) => ({ ...s, [postId]: wasStarred }));
      setPosts((list) => list.map((p) => (p.id === postId
        ? { ...p, counts: { ...p.counts, stars: p.counts.stars + (wasStarred ? 1 : -1) } }
        : p)));
      throw err;
    }
  }, [starred]);

  // --- Galaxies ------------------------------------------------------------
  const loadGalaxies = useCallback(async () => {
    if (!authUser) return;
    const rows = await fetchMyGalaxies(authUser.id);
    setGalaxies(rows);
  }, [authUser]);

  const createGalaxy = useCallback(async (name) => {
    const g = await apiCreateGalaxy(name);
    setGalaxies((gs) => [...gs, g]);
    return g;
  }, []);

  const loadPublicGalaxies = useCallback(async (options = { excludeMine: true }) => {
    setPublicGalaxiesLoading(true);
    try {
      const rows = await apiFetchPublicGalaxies(options);
      setPublicGalaxies(rows);
      return rows;
    } finally {
      setPublicGalaxiesLoading(false);
    }
  }, []);

  const joinPublicGalaxy = useCallback(async (galaxyId) => {
    await apiJoinGalaxy(galaxyId);
    setPublicGalaxies((rows) => rows.map((row) => (row.id === galaxyId
      ? { ...row, joined: true, memberCount: row.memberCount + 1 }
      : row)));
    await loadGalaxies();
  }, [loadGalaxies]);

  // --- Friends -------------------------------------------------------------
  const loadFriends = useCallback(async () => {
    if (!authUser) return;
    const rows = await apiFetchFriends(authUser.id);
    setFriends(rows);
  }, [authUser]);

  const addFriendById = useCallback(async (friendId) => {
    await apiAddFriend(friendId);
    await loadFriends();
  }, [loadFriends]);

  const removeFriendById = useCallback(async (friendId) => {
    await apiRemoveFriend(friendId);
    await loadFriends();
  }, [loadFriends]);

  // --- Notifications -------------------------------------------------------
  const loadNotifications = useCallback(async () => {
    if (!authUser) return [];
    setNotificationsLoading(true);
    try {
      const rows = await apiFetchNotifications(authUser.id);
      setNotifications(rows);
      return rows;
    } finally {
      setNotificationsLoading(false);
    }
  }, [authUser]);

  const markNotificationReadById = useCallback(async (id) => {
    await apiMarkNotificationRead(id);
    setNotifications((rows) => rows.map((row) => (row.id === id ? { ...row, read: true } : row)));
  }, []);

  const unreadNotifCount = notifications.filter((notification) => !notification.read).length;

  useEffect(() => {
    if (authUser && profile) {
      loadGalaxies();
      loadFriends();
      loadNotifications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, profile]);

  const value = useMemo(() => ({
    // auth
    authUser, profile, authLoading, needsSetup,
    login, signup, logout, completeProfile, saveProfile,
    user: profile ? { name: profile.display_name, handle: profile.username, avatar: profile.avatar_url } : null,
    // posts
    posts, postsLoading, loadPosts, loadMyPosts, addPost, toggleStar, starred,
    // galaxies / friends
    galaxies, loadGalaxies, createGalaxy,
    publicGalaxies, publicGalaxiesLoading, loadPublicGalaxies, joinPublicGalaxy,
    friends, loadFriends, addFriendById, removeFriendById,
    // notifications
    notifications, notificationsLoading, loadNotifications, markNotificationReadById, unreadNotifCount,
    // settings
    screenLimit, setScreenLimit, usedMinutes, resetInMs,
  }), [authUser, profile, authLoading, needsSetup, login, signup, logout, completeProfile, saveProfile,
    posts, postsLoading, loadPosts, loadMyPosts, addPost, toggleStar, starred,
    galaxies, loadGalaxies, createGalaxy,
    publicGalaxies, publicGalaxiesLoading, loadPublicGalaxies, joinPublicGalaxy,
    friends, loadFriends, addFriendById, removeFriendById,
    notifications, notificationsLoading, loadNotifications, markNotificationReadById, unreadNotifCount,
    screenLimit, usedMinutes, resetInMs]);

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
